import cn from 'classnames'

import { ClientOnly, getRouteApi } from '@tanstack/react-router'
import { ArcSmall } from '~/components/data-display/charts/ArcSmall'
import { LineSmall, LineSmallData, LineSmallFallback } from '~/components/data-display/charts/LineSmall'
import { HTMLProps, Suspense } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/data-display/tooltip/Tooltip'

const fmtUSD = (v: number, decimals: number = 2) =>
{
    const f = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currencyDisplay: 'code',
        currency: 'USD',
        notation: 'compact',
        compactDisplay: 'long',
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
    })
    return f.format(v).replace(/^(-)?([A-Z]{3})\s*(.+)$/, '$1$3 $2')
}

const fBTC = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currencyDisplay: 'code',
    currency: 'BTC',
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
})

const fmtBTC = (v: number) => fBTC.format(v).replace(/^(-)?([A-Z]{3})\s*(.+)$/, '$1$3 $2')

const fmtTicker = (v: number, ticker: string, decimals: number = 0) =>
{
    const f = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currencyDisplay: 'code',
        currency: 'usd',
        notation: 'compact',
        compactDisplay: 'long',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })
    return f.format(v).replace(/^(-)?([A-Z]{3})\s*(.+)$/, `$1$3 ${ticker}`)
}

interface TableBodyProps extends HTMLProps<HTMLTableSectionElement> {}

export function Body (props: TableBodyProps)
{
    const {
        stats,
        prices,
    } = getRouteApi('/').useLoaderData()

    const mcaps = Object.entries(stats.coins).reduce((m, [_, item]) =>
    {
        m.total += item.marketCap
        m.shielded += parseFloat(item.shielded) * item.price
        return m
    }, {
        total: 0,
        shielded: 0,
    })

    return (
        <tbody>
        <Suspense
            fallback={<tr>
                <td colSpan={5}>Loading...</td>
            </tr>}
        >
            {Object.entries(stats.coins)
                .sort(([lk, {
                    marketCap: mcapl,
                    shieldedSupply: shieldedl,
                }], [rk, {
                    marketCap: mcapr,
                    shieldedSupply: shieldedr,
                }]) => mcapr * parseFloat(shieldedr) - mcapl * parseFloat(shieldedl))
                .map(([key, value], idx) =>
                {
                    const meta = JSON.parse(value.meta?.allDataFrom || '{}')

                    return (
                        <tr key={key} className={'border-b-transparent'}>
                            <Name
                                idx={idx}
                                slug={key}
                                ticker={meta?.['Ticker'].Value || key.slice(0, 3).toUpperCase()}
                                title={meta ?.['Name'].Value || (
                                       key.at(0)?.toUpperCase() || ''
                                ) + key.slice(1)}
                            />

                            <Price usd={value.price} btc={value.priceVsBtc} />

                            <MarketCap
                                usd={value.marketCap}
                                circulation={value.circulation}
                                shielded={parseFloat(value.shieldedSupply) / 100}
                                ticker={meta?.['Ticker'].Value || key.slice(0, 3).toUpperCase()}
                            />

                            <ShieldedChart// circulation={value.circulation}
                                shielded={parseFloat(value.shieldedSupply)}
                            />

                            <Price30DChart prices={prices?.prices[key]?.prices || []} />

                            <PriceBTC30DChart prices={prices?.prices[key]?.prices || []} />

                            {/*<PrivacyMethods meta={meta} />*/}

                            <DominanceChart value={parseFloat(value.shielded) * value.price / mcaps.shielded * 100} />
                        </tr>
                    )
                })}
        </Suspense>
        </tbody>
    )
}

function TitleMobile ({
    children,
    ...props
}: HTMLProps<HTMLDivElement>)
{
    return <div
        className={cn('text-start whitespace-nowrap overflow-hidden overflow-ellipsis', 'md:hidden')}
    >{children}</div>
}

interface TableCellProps extends HTMLProps<HTMLTableCellElement> {}

interface NameProps extends TableCellProps
{
    idx: number
    slug: string
    title: string
    ticker: string
}

function Name ({
    idx,
    slug,
    title,
    ticker,
    ...props
}: NameProps)
{
    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 border-b rounded-b-lg px-2 py-1.5 bg-accent/4', // px-4 with
                // title
                'md:h-1 md:py-2 md:ps-4 md:pe-6 md:align-middle md:text-start md:rounded-be-none md:bg-transparent',
            )}
        >
            {/*<TitleMobile>Name</TitleMobile>*/}

            <div className={'h-full w-full grow-0 flex flex-row justify-between items-center gap-4 md:min-w-max md:justify-start'}>
                <p className={cn('order-2 whitespace-nowrap overflow-ellipsis overflow-hidden', 'md:order-1')}>
                    <span className={cn('hidden', 'md:inline')}>{idx + 1}&nbsp;&nbsp;</span>{title}&nbsp;

                    <br className={'hidden md:inline'} />

                    <span className={'text-accent/40'}><span
                        className={cn('hidden opacity-0 invisible', 'md:inline')}
                        aria-hidden={true}
                    >{idx + 1}&nbsp;&nbsp;</span>{ticker}</span>
                </p>
                <img
                    src={`/images/blockchains/${slug}.webp`}
                    srcSet={`/images/blockchains/${slug}@2x.webp 2x`}
                    alt={`${title} logo`}
                    width={60}
                    height={60}
                    loading={'lazy'}
                    fetchPriority={'low'}
                    decoding={'async'}
                    className={cn('shrink-0 order-1 h-18 w-auto', 'md:ms-auto md:order-2 md:h-15')}
                />
            </div>
        </td>
    )
}

interface PriceProps extends TableCellProps
{
    usd: number
    btc: number
}

function Price ({
    usd,
    btc,
}: PriceProps)
{
    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:px-5',
                '2xl:px-6',
            )}
        >
            <TitleMobile>Price</TitleMobile>

            <div className={cn('whitespace-nowrap')}>
                {fmtUSD(usd)}<br /><span className={'text-accent/40'}>{fmtBTC(btc)}</span>
            </div>
        </td>
    )
}

interface MarketCapProps extends TableCellProps
{
    usd: number
    circulation: number
    shielded: number
    ticker: string
}

function MarketCap ({
    usd,
    circulation,
    shielded,
    ticker,
}: MarketCapProps)
{
    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:px-5',
                '2xl:px-6',
            )}
        >
            <TitleMobile>Shielded Market cap</TitleMobile>

            <div className={cn('whitespace-nowrap')}>

                <ClientOnly
                    fallback={<div className={cn('inline-block ms-1 align-baseline text-end')}>
                        {fmtUSD(usd * shielded)}<br /><span className={'text-accent/40'}>{fmtTicker(
                        circulation * shielded,
                        ticker,
                        2,
                    )}</span>
                    </div>}
                >

                    <Tooltip>

                        <TooltipTrigger className={cn('inline-block ms-1 align-baseline text-end')}>
                            {fmtUSD(usd * shielded)}<br /><span className={'text-accent/40'}>{fmtTicker(
                            circulation * shielded,
                            ticker,
                            2,
                        )}</span>
                        </TooltipTrigger>

                        <TooltipContent>
                            <div className={'tooltip w-full max-w-prose'}>
                                Total:<br />
                                <span className={'text-end'}>{fmtUSD(usd)}</span><br /><span className={'text-accent/40 text-end'}>{fmtTicker(circulation,
                                ticker,
                                2,
                            )}</span>
                            </div>
                        </TooltipContent>

                    </Tooltip>

                </ClientOnly>
            </div>
        </td>
    )
}

interface ShieldedChartProps extends TableCellProps
{
    // circulation: number
    shielded: number
}

function ShieldedChart ({
    // circulation,
    shielded,
}: ShieldedChartProps)
{
    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:px-5',
                '2xl:px-6',
            )}
        >
            <TitleMobile>Shielded %</TitleMobile>

            <div
                className={cn('ms-auto w-full max-w-31 min-h-6', 'md:mx-auto md:max-w-none md:min-w-25 md:min-h-12.5')}
            >
                <ClientOnly
                    fallback={<div
                        className={cn(
                            'w-full h-6 text-center',
                            'md:mx-auto md:w-25 md:h-12.5',
                        )}
                    >{Math.round(shielded * 100) / 100}%</div>}
                > <ArcSmall
                    min={0}// max={circulation}
                    max={100}
                    value={shielded}
                    className={cn('ms-auto w-full h-6', 'md:mx-auto md:max-w-none md:w-25 md:h-12.5')}
                /> </ClientOnly>
            </div>
        </td>
    )
}

interface Price30DChartProps extends TableCellProps
{
    // todo: infer from schema
    prices: {
        timestamp: string
        low: string
        high: string
        last: string
    }[]
}

function Price30DChart ({ prices }: Price30DChartProps)
{
    const lineData: LineSmallData = prices.map(i => [new Date(parseInt(i.timestamp) * 1000), parseFloat(i.last)])

    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:px-5',
                '2xl:px-6',
            )}
        >
            <TitleMobile>Price USD 30D</TitleMobile>

            <div
                className={cn(
                    'ms-auto w-full max-w-40 min-h-15',
                    'md:max-w-45',
                    'xl:min-w-45 xl:max-w-none',
                    '2xl:min-w-50',
                )}
            >
                <ClientOnly
                    fallback={<LineSmallFallback
                        data={lineData}
                        className={cn(
                            'ms-auto w-full h-15',
                            'md:w-45',
                            '2xl:w-50',
                            'normal-case',
                        )}
                    />}
                >

                    <LineSmall
                        data={lineData}
                        className={cn('ms-auto w-full h-15', 'md:w-45', '2xl:w-50', 'normal-case')}
                    />

                </ClientOnly>
            </div>
        </td>
    )
}

interface PriceBTC30DChartProps extends TableCellProps
{
    // todo: infer from schema
    prices: {
        timestamp: string
        low: string
        high: string
        last: string
        lastBtc: string
    }[]
}

function PriceBTC30DChart ({ prices }: PriceBTC30DChartProps)
{
    const lineData: LineSmallData = prices.map(i => [new Date(parseInt(i.timestamp) * 1000), parseFloat(i.lastBtc)])

    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:px-5',
                '2xl:px-6',
            )}
        >
            <TitleMobile>Price BTC 30D</TitleMobile>

            <div
                className={cn(
                    'ms-auto w-full max-w-40 min-h-15',
                    'md:max-w-45',
                    'xl:min-w-45 xl:max-w-none',
                    '2xl:min-w-50',
                )}
            >
                <ClientOnly
                    fallback={<LineSmallFallback
                        valueFmtOptions={{
                            decimals: 8,
                            prefix: 'BTC ',
                        }}
                        data={lineData}
                        className={cn(
                            'ms-auto w-full h-15',
                            'md:w-45',
                            '2xl:w-50',
                            'normal-case',
                        )}
                    />}
                >

                    <LineSmall
                        valueFmtOptions={{
                            decimals: 8,
                            prefix: 'BTC ',
                        }}
                        data={lineData}
                        className={cn('ms-auto w-full h-15', 'md:w-45', '2xl:w-50', 'normal-case')}
                    />

                </ClientOnly>
            </div>
        </td>
    )
}

// interface PrivacyMethodsProps extends TableCellProps
// {
//     meta: any
// }
//
// function PrivacyMethods ({ meta }: PrivacyMethodsProps)
// {
//     const methods = meta?.['Privacy Method'].Value || '?'
//
//     return (
//         <td
//             className={cn(
//                 'row items-center justify-between gap-3 text-end p-3',
//                 'md:align-middle md:py-2 md:px-5',
//                 '2xl:px-6',
//             )}
//         >
//             <TitleMobile>Privacy method</TitleMobile>
//
//             <div className={cn('text-gradient-accent 2xl:min-w-42')}>
//                 {methods}
//             </div>
//         </td>
//     )
// }

interface DominanceChartProps extends TableCellProps
{
    value: number
}

function DominanceChart ({ value }: DominanceChartProps)
{
    return (
        <td
            className={cn(
                'row items-center justify-between gap-3 text-end p-3',
                'md:align-middle md:py-2 md:ps-6 md:pe-4',
            )}
        >
            <TitleMobile>Dominance %</TitleMobile>

            <div
                className={cn('ms-auto w-full max-w-31 min-h-6', 'md:mx-auto md:max-w-none md:min-w-25 md:min-h-12.5')}
            >
                <ClientOnly
                    fallback={<div
                        className={cn(
                            'ms-auto w-full h-6 text-center',
                            'md:mx-auto md:w-25 md:h-12.5',
                        )}
                    >{Math.round(value * 100) / 100}%</div>}
                > <ArcSmall
                    min={0}
                    max={100}
                    value={value}
                    className={cn('ms-auto w-full h-6', 'md:mx-auto md:max-w-none md:w-25 md:h-12.5')}
                /> </ClientOnly>
            </div>
        </td>
    )
}